import React, { PropsWithChildren, useEffect, useState } from "react";
import { Layout, Popover } from "antd";
import { Drawer } from "antd";
import { useAtomValue } from "jotai";
import Avatar from "antd/es/avatar/avatar";
import { format } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import SideBar from "./sidebar";
import HeaderCard from "./headerCard/headerCard";
import styles from "./main.module.css";
import { logout, profileDetails, userAtom } from "../../utils/store";
import NotificationCard from "./notificationCard/notificationCard";

import { ReactComponent as ArrowDown } from "../../assets/svgs/angle-down.svg";
import { ReactComponent as NotiBell } from "../../assets/svgs/bell.svg";
import { ReactComponent as Harmbugger } from "../../assets/svgs/hambugger.svg";
import apiCall from "../../utils/apiCall";
import { NotificationResponse } from "./notificationCard/type";
import { Loader } from "../../customs";

interface ComponentProp extends PropsWithChildren {
  title?: React.ReactNode;
  pageHeading?: string;
}

const { Content } = Layout;

const CLayout: React.FC<ComponentProp> = ({ children, title, pageHeading }) => {
  const [open, setOpen] = useState([false, false]);
  const userDetails = useAtomValue(profileDetails);
  const user = useAtomValue(userAtom);

  const queryClient = useQueryClient();

  const isTokenExpired = (expiration: string) => {
    const expiryDate = new Date(expiration);
    const currentDate = new Date();
    return currentDate >= expiryDate;
  };  

  useEffect(() => {
    const interval = setInterval(() => {
      if (user?.Expiration && isTokenExpired(user?.Expiration)) {
        logout();
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, [user]);

  const toggleDrawer = (idx: number, target: boolean) => {
    setOpen((p) => {
      p[idx] = target;
      return [...p];
    });
  };

  const currentDate = new Date();
  const formattedDate = format(currentDate, "do MMMM, yyyy");

  const getExamNotification = async (): Promise<NotificationResponse> => {
    return (
      await apiCall().get(`/Member/GetNotication?membership=${user?.MemberId}`)
    )?.data;
  };

  const { data, isLoading } = useQuery({
    queryKey: ["get-exam-notification", user?.MemberId],
    queryFn: getExamNotification,
  });

  const updateNotification = async (id: number) => {
    return (await apiCall().post(`/Activity/UpdateNotication?Id=${id}`))?.data;
  };

  const updateNotificationMutation = useMutation({
    mutationFn: updateNotification,
  });

  const handleUpdateNotification = async (id: number) => {
    await updateNotificationMutation.mutateAsync(id, {
      onSuccess: () => {
        queryClient.refetchQueries({ queryKey: ["get-exam-notification"] });
      },
    });
  };

  const notificationData = data?.Data ?? [];

  const filterActiveNotification = notificationData?.filter(
    (item) => item?.IsActive === true
  );

  if (isLoading) <Loader />;

  return (
    <main className={styles.main}>
      <Drawer
        placement="left"
        onClose={() => toggleDrawer(0, false)}
        open={open[0]}
        className={styles.drawer}
        width={"300px"}
        styles={{ body: { padding: "0px" } }}
      >
        <SideBar className={styles.drawSideBar} />
      </Drawer>

      <SideBar className={styles.sideBar} />

      <div className={styles.superHeader}>
        <div className={styles.header}>
          <div>
            <Harmbugger
              onClick={() => toggleDrawer(0, true)}
              className={styles.harmbugger}
            />

            <p className={styles.pageHeading}>{pageHeading}</p>
          </div>

          <div>
            <span>{formattedDate}</span>

            <div className={styles.wrapper}>
              <NotiBell />

              {filterActiveNotification?.length > 0 && (
                <div className={styles.hoveringCircle}>
                  {filterActiveNotification?.length > 9
                    ? "9+"
                    : filterActiveNotification?.length}
                </div>
              )}

              <div className={styles.popover}>
                <NotificationCard
                  data={filterActiveNotification}
                  onClick={handleUpdateNotification}
                />
              </div>
            </div>

            <div className={styles.pill}>
              <Avatar src={userDetails?.UploadPictureUrl} size={29} />

              <Popover
                placement="bottomRight"
                content={
                  <HeaderCard
                    profilePicture={userDetails?.UploadPictureUrl}
                    firstName={userDetails?.FirstName}
                  />
                }
              >
                <ArrowDown />
              </Popover>
            </div>
          </div>
        </div>

        <Content
          className={styles.content}
          style={{
            paddingBlock: 24,
            margin: 0,
            minHeight: 280,
            overflowX: "hidden",
            overflowY: "auto",
            backgroundColor: "#F3F3F3",
          }}
        >
          {pageHeading && (
            <div>
              <p className={styles.title}>{title}</p>
            </div>
          )}
          {children}
        </Content>
      </div>
    </main>
  );
};

export default CLayout;
